<template>
  <div v-if="question" class="flex flex-row">
    <TextAnswer
      :id="question.id"
      :key="question.id"
      :label="question.label"
      :required="question.required"
      v-mask="question.mask"
      :placeholder="question.label"
      v-model="question.value"
      v-if="question.field_type.label === 'text'"
      :errors="errors[question.id]"
      :maxlength="question.max"
      :question="question"
    ></TextAnswer>
    <NumberAnswer
      :id="question.id"
      :key="question.id"
      :label="question.label"
      :required="question.required"
      v-mask="question.mask"
      :placeholder="question.label"
      v-model="question.value"
      v-if="question.field_type.label === 'number'"
      :errors="errors[question.id]"
      :maxlength="question.max"
      :question="question"
    ></NumberAnswer>
    <DateAnswer
      :id="question.id"
      :key="question.id"
      :label="question.label"
      :required="question.required"
      :placeholder="question.label"
      v-model="question.value"
      v-if="['datetime', 'date'].includes(question.field_type.label)"
      :errors="errors[question.id]"
      :question="question"
    ></DateAnswer>
    <TextareaAnswer
      v-if="question.field_type.label === 'textarea'"
      :id="question.id"
      :key="question.id"
      :label="question.label"
      :placeholder="question.label"
      :required="question.required"
      v-model="question.value"
      :errors="errors[question.id]"
      :maxlength="question.max"
      :question="question"
    ></TextareaAnswer>
    <MultiselectAnswer
      :id="question.id"
      v-if="question.field_type.label === 'multiselect'"
      :options="question.options"
      :label="question.label"
      :required="question.required"
      optionLabel="label"
      optionValueProp="label"
      mode="tags"
      :placeholder="question.label"
      v-model="question.value"
      :errors="errors[question.id]"
      :question="question"
    ></MultiselectAnswer>
    <SelectAnswer
      :id="question.id"
      v-if="question.field_type.label === 'select'"
      :options="question.options"
      :label="question.label"
      :required="question.required"
      optionLabel="label"
      optionValueProp="label"
      :placeholder="question.label"
      v-model="question.value"
      :errors="errors[question.id]"
      :question="question"
    ></SelectAnswer>
    <CheckboxAnswer
      :id="question.id"
      v-if="question.field_type.label === 'checkbox'"
      :name="question.id"
      :required="question.required"
      :label="question.label"
      v-model="question.value"
      :errors="errors[question.id]"
      :question="question"
    ></CheckboxAnswer>
    <CheckboxGroupAnswer
      :id="question.id"
      v-if="question.field_type.label === 'checkbox_group'"
      :name="question.id"
      :required="question.required"
      v-model="question.value"
      :errors="errors[question.id]"
      :question="question"
    ></CheckboxGroupAnswer>
    <RadioAnswer
      :id="question.id"
      v-if="question.field_type.label === 'radio'"
      :name="question.id"
      :required="question.required"
      v-model="question.value"
      :options="question.options"
      :errors="errors[question.id]"
      type="radio"
      :question="question"
    ></RadioAnswer>
    <FileAnswer
      type="file"
      @uploaded="upload(question, $event)"
      v-model="question.value"
      name="file"
      :url="`/api/v1/questions/${question.id}/upload`"
      v-if="question.field_type.label === 'file'"
      :multiple="true"
      :accept="inputFileAccept()"
      :question="question"
    >
      <span class="px-2">
        Apenas <span class="text-danger fw-bold">{{ inputFileAccept() }}</span> com até
        <span class="text-danger fw-bold">{{ question.max }} MB</span> .
      </span>
    </FileAnswer>
  </div>
</template>


<script>

import { useTranslations } from "@/src/composables/translations";
import { useVuelidate } from "@vuelidate/core";
import { reactive, toRef, watch, ref, computed } from "vue";
import { requiredIf, helpers, minLength, maxLength } from "@vuelidate/validators";
import { useStore } from "vuex";
import questionStore from "#repositories/questionRepository";

import CheckboxAnswer from "#views/questionnaire/answers/CheckboxAnswer";
import CheckboxGroupAnswer from "#views/questionnaire/answers/CheckboxGroupAnswer";
import DateAnswer from "#views/questionnaire/answers/DateAnswer";
import EditorAnswer from "#views/questionnaire/answers/EditorAnswer";
import FileAnswer from "#views/questionnaire/answers/FileAnswer";
import MultiselectAnswer from "#views/questionnaire/answers/MultiselectAnswer";
import NumberAnswer from "#views/questionnaire/answers/NumberAnswer";
import RadioAnswer from "#views/questionnaire/answers/RadioAnswer";
import SelectAnswer from "#views/questionnaire/answers/SelectAnswer";
import TextAnswer from "#views/questionnaire/answers/TextAnswer";
import TextareaAnswer from "#views/questionnaire/answers/TextareaAnswer";

export default {
  name: "question",
  props: {
    questionObject: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
    },
  },
  components: {
    CheckboxAnswer,
    CheckboxGroupAnswer,
    DateAnswer,
    EditorAnswer,
    FileAnswer,
    MultiselectAnswer,
    NumberAnswer,
    RadioAnswer,
    SelectAnswer,
    TextAnswer,
    TextareaAnswer,
  },
  setup(props, { emit }) {
    const { translateValidation } = useTranslations();
    const question = reactive(toRef(props, "questionObject"));
    const store = useStore();
    const progress = computed(() => {
      if (store.state.filesStore.question_id === props.questionObject.id) {
        return store.state.filesStore.progress;
      }
    });
    const uploadComplete = ref(false);
    const errors = ref({});

    if (props.questionObject.field_type.label === "multiselect") {
      question.value.value = [];
    }

    const customRequired = (id) => {
      return helpers.withParams(
        { type: "required", id: id },
        requiredIf(() => {
          return props.questionObject.required;
        })
      );
    };

    const rules = {
      question: {
        value: {
          required: helpers.withMessage(
            translateValidation("required"),
            customRequired(question.value.id)
          ),
          min: helpers.withMessage(
            `O campo ${question.value.label} deve ter no mínimo ${question.value.min} caracteres`,
            minLength(question.value.min)
          ),
        },
      },
    };

    const v$ = useVuelidate(rules, { question });

    function inputFileAccept() {
      if (props.questionObject.mimes) {
        let extensions = props.questionObject.mimes.map((value) => `.${value}`);
        return extensions.join(",");
      }
    }

    function upload(question, file) {
      question.value = file;
    }

    async function clear(questionFile) {
      const payload = {
        id: questionFile.question_id,
        file_path: questionFile.file.file_path,
        file_name: questionFile.file.file_name,
      };
      question.value.value = null;
      uploadComplete.value = false;
      await store.commit("setProgress", 0);
      await questionStore.deleteQuestionFile(payload.id, payload);
      question.value.value = null;
      uploadComplete.value = true;
      await store.commit("setProgress", 100);
    }

    function download(url, title) {
      store.dispatch("downloadFile", { url: url }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", title);
        document.body.appendChild(link);
        link.click();
      });
    }

    watch(
      () => question,
      (current, previous) => {
        emit("questionUpdate", current.value);
      },
      {
        deep: true,
      }
    );

    return {
      v$,
      question,
      errors,
      progress,
      uploadComplete,
      inputFileAccept,
      upload,
      download,
      clear,
    };
  },
};
</script>

<style scoped></style>
